import React, { useEffect, useMemo, useState } from 'react';
import {
  IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCheckbox, IonChip, IonInput, IonItem,
  IonLabel, IonModal, IonNote, IonSelect, IonSelectOption, IonTextarea,
} from '@ionic/react';
import {
  LaborLineInput, MaterialLineInput, ServiceCustomer, ServiceEstimate, ServiceEstimateWrite, ServiceSettingsBundle,
} from '../../../interfaces/ServiceEstimate';
import { calcServiceEstimate, ratesFromSettings } from '../../../utils/calcServiceEstimate';
import { serviceApi } from '../../../utils/serviceEstimatesApi';
import AcceptancePanel from './AcceptancePanel';
import CustomerPicker from './CustomerPicker';
import { ACS_PATTERN, STATUS_COLOR, STATUS_LABEL, money, pct, shortDate, todayIso } from './serviceFormat';
import './EstimateEditor.css';

interface Props {
  bundle: ServiceSettingsBundle;
  estimate: ServiceEstimate | null;
  onSaved: (e: ServiceEstimate) => void;
  onNew: () => void;
  notify: (msg: string) => void;
}

interface Draft {
  customer: ServiceCustomer | null;
  acs: string;
  contactName: string;
  contactEmail: string;
  date: string;
  scope: string;
  assumptions: string[];
  location: string; // tax location id, 'exempt', or ''
  locationAuto: boolean;
  freightOn: boolean;
  freightPct: string;
  discount: string;
  materials: MaterialLineInput[];
  labor: LaborLineInput[];
}

const blankMaterial = (): MaterialLineInput => ({ description: '', alpha_cost: '' });
const blankLabor = (): LaborLineInput => ({ description: '', hours: '', labor_type: 'ME-Preferred' });

const fromEstimate = (e: ServiceEstimate): Draft => ({
  customer: e.customer_detail,
  acs: e.acs_number || '',
  contactName: e.contact_name,
  contactEmail: e.contact_email,
  date: e.estimate_date,
  scope: e.scope_of_work,
  assumptions: e.assumptions,
  location: e.tax_exempt ? 'exempt' : e.tax_location ? String(e.tax_location) : '',
  locationAuto: false,
  freightOn: e.freight_on,
  freightPct: String(Math.round(Number(e.freight_rate) * 1e6) / 1e4),
  discount: Number(e.discount) ? String(Number(e.discount)) : '',
  materials: e.material_lines.length ? e.material_lines.map((m) => ({ description: m.description, alpha_cost: String(Number(m.alpha_cost)) })) : [blankMaterial()],
  labor: e.labor_lines.length ? e.labor_lines.map((l) => ({ description: l.description, hours: String(Number(l.hours)), labor_type: l.labor_type })) : [blankLabor()],
});

const blankDraft = (b: ServiceSettingsBundle): Draft => ({
  customer: null, acs: '', contactName: '', contactEmail: '', date: todayIso(), scope: '',
  assumptions: [b.settings.default_assumption], location: '', locationAuto: false, freightOn: true,
  freightPct: String(Math.round(Number(b.settings.default_freight_rate) * 1e6) / 1e4), discount: '',
  materials: [blankMaterial()], labor: [blankLabor()],
});

const EstimateEditor: React.FC<Props> = ({ bundle, estimate, onSaved, onNew, notify }) => {
  const [d, setD] = useState<Draft>(() => (estimate ? fromEstimate(estimate) : blankDraft(bundle)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState({ to: '', cc: '', message: '' });

  useEffect(() => {
    setD(estimate ? fromEstimate(estimate) : blankDraft(bundle));
    setError('');
  }, [estimate, bundle]);

  const locked = !!estimate && (estimate.status === 'accepted' || estimate.status === 'declined');
  const rates = useMemo(() => ratesFromSettings(bundle), [bundle]);
  const locations = bundle.tax_locations;
  const taxRate = d.location === 'exempt' || !d.location ? 0 : Number(locations.find((l) => String(l.id) === d.location)?.rate || 0);
  const c = calcServiceEstimate({
    materials: d.materials.map((m) => ({ description: m.description, alphaCost: Number(m.alpha_cost) || 0 })),
    labor: d.labor.map((l) => ({ description: l.description, hours: Number(l.hours) || 0, laborType: l.labor_type })),
    taxRate, freightOn: d.freightOn, freightRate: (Number(d.freightPct) || 0) / 100, discount: Number(d.discount) || 0,
  }, rates);

  const set = (p: Partial<Draft>) => setD((prev) => ({ ...prev, ...p }));
  const usual = d.customer ? (d.customer.tax_exempt ? 'exempt' : d.customer.tax_location ? String(d.customer.tax_location) : '') : '';

  const chooseCustomer = (cust: ServiceCustomer | null) => {
    if (!cust) {
      set({ customer: null, ...(d.locationAuto ? { location: '', locationAuto: false } : {}) });
      return;
    }
    const auto = cust.tax_exempt ? 'exempt' : cust.tax_location ? String(cust.tax_location) : '';
    set({
      customer: cust,
      contactName: d.contactName || cust.contact_name,
      contactEmail: d.contactEmail || cust.contact_email,
      ...(auto && (!d.location || d.locationAuto) ? { location: auto, locationAuto: true } : {}),
    });
  };

  let locNote = '';
  let locClass = '';
  if (d.locationAuto && d.customer) { locNote = `Applied automatically from ${d.customer.name}'s customer record. Change it only if this job is at a different site.`; locClass = 'good'; }
  else if (d.customer && usual && d.location && d.location !== usual) { locNote = `This differs from ${d.customer.name}'s usual location (${d.customer.tax_location_label}).`; locClass = 'warn'; }
  else if (d.customer && !usual) locNote = "New customer: pick where the work is done. It's saved to the customer record, so next time it fills in by itself.";
  else if (d.location === 'exempt') locNote = "No sales tax. Keep the customer's exemption certificate on file.";
  if (!locations.length) { locNote = 'No tax locations are set up yet. Admin or Director adds them in Settings.'; locClass = 'warn'; }

  const toWrite = (): ServiceEstimateWrite => ({
    customer: d.customer ? d.customer.id : '',
    acs_number: d.acs.trim() ? d.acs.trim().toUpperCase() : null,
    contact_name: d.contactName, contact_email: d.contactEmail, estimate_date: d.date, scope_of_work: d.scope,
    assumptions: d.assumptions.filter((a) => a.trim()),
    tax_location: d.location && d.location !== 'exempt' ? Number(d.location) : null, tax_exempt: d.location === 'exempt',
    freight_on: d.freightOn, freight_rate: String((Number(d.freightPct) || 0) / 100), discount: String(Number(d.discount) || 0),
    materials: d.materials, labor: d.labor,
  });

  const save = async () => {
    if (!d.customer) { setError('Pick or add the customer first.'); return; }
    if (d.acs.trim() && !ACS_PATTERN.test(d.acs.trim().toUpperCase())) { setError('Enter the ACS# as ACS26-0001, or leave it blank for now.'); return; }
    setBusy(true);
    setError('');
    try {
      const saved = estimate ? await serviceApi.updateEstimate(estimate.id, toWrite()) : await serviceApi.createEstimate(toWrite());
      onSaved(saved);
      notify(saved.acs_number ? `Saved ${saved.acs_number}` : 'Saved as a draft');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const openPdf = async () => {
    if (!estimate) return;
    try {
      const blob = await serviceApi.proposalPdf(estimate.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank'); // print or save from the PDF viewer
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      onSaved(await serviceApi.getEstimate(estimate.id));
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Could not make the PDF.');
    }
  };

  const sendEmail = async () => {
    if (!estimate) return;
    try {
      const r = await serviceApi.emailProposal(estimate.id, email.to, email.cc, email.message);
      setEmailOpen(false);
      notify(r.detail);
      onSaved(await serviceApi.getEstimate(estimate.id));
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Could not send the email.');
    }
  };

  const canPropose = !!estimate && !!estimate.acs_number && !estimate.is_expired && estimate.status !== 'declined';
  const statusKey = estimate ? (estimate.is_expired ? 'expired' : estimate.status) : 'draft';

  return (
    <div className="estimate-editor">
      {locked && estimate && (
        <div className={`estimate-banner ${estimate.status === 'declined' ? 'bad' : 'good'}`}>
          {estimate.status === 'accepted'
            ? `Signed and accepted by ${estimate.accepted_name} on ${shortDate(estimate.accepted_at)}. The estimate is locked; start a new estimate for any change.`
            : 'The customer declined this proposal. The estimate is locked; start a new estimate to re-quote.'}
        </div>
      )}

      <IonCard>
        <IonCardHeader className="estimate-status-head">
          <IonCardTitle>Status</IonCardTitle>
          <IonChip color={STATUS_COLOR[statusKey]}>{STATUS_LABEL[statusKey]}</IonChip>
          {estimate?.acs_number && <strong>{estimate.acs_number}</strong>}
        </IonCardHeader>
        <IonCardContent>
          <IonItem>
            <IonInput label="ACS# (from the office)" labelPlacement="stacked" placeholder="ACS26-0001" value={d.acs} disabled={locked}
              onIonInput={(e) => set({ acs: String(e.detail.value ?? '') })} />
          </IonItem>
          <p className="estimate-note">
            {!d.acs.trim() ? 'Save as a draft now; enter the ACS# when the office gives it to you. The PDF and signature need it.'
              : estimate?.is_expired ? `Expired on ${shortDate(estimate.valid_until)}. Change the date to re-issue it.`
                : estimate ? `Valid until ${shortDate(estimate.valid_until)}.` : ''}
          </p>
          <div className="estimate-actions">
            <IonButton onClick={save} disabled={busy || locked}>{estimate ? 'Save changes' : 'Save estimate'}</IonButton>
            <IonButton fill="outline" onClick={onNew}>New estimate</IonButton>
          </div>
          {error && <p className="estimate-error">{error}</p>}
        </IonCardContent>
      </IonCard>

      <fieldset className="estimate-fields" disabled={locked}>
        <IonCard>
          <IonCardHeader><IonCardTitle>Customer</IonCardTitle></IonCardHeader>
          <IonCardContent>
            <CustomerPicker value={d.customer} disabled={locked} onChange={chooseCustomer} />
            <div className="estimate-grid">
              <IonItem><IonInput label="Customer contact" labelPlacement="stacked" value={d.contactName} onIonInput={(e) => set({ contactName: String(e.detail.value ?? '') })} /></IonItem>
              <IonItem><IonInput label="Contact email" labelPlacement="stacked" type="email" value={d.contactEmail} onIonInput={(e) => set({ contactEmail: String(e.detail.value ?? '') })} /></IonItem>
              <IonItem><IonInput label="Date of estimate" labelPlacement="stacked" type="date" value={d.date} onIonInput={(e) => set({ date: String(e.detail.value ?? '') })} /></IonItem>
              <IonItem>
                <IonSelect label="Job location for sales tax" labelPlacement="stacked" value={d.location} interface="action-sheet"
                  onIonChange={(e) => set({ location: String(e.detail.value ?? ''), locationAuto: false })}>
                  {locations.map((l) => <IonSelectOption key={l.id} value={String(l.id)}>{`${l.label} — ${(Number(l.rate) * 100).toFixed(2)}%`}</IonSelectOption>)}
                  <IonSelectOption value="exempt">Tax exempt (certificate on file) — 0.00%</IonSelectOption>
                </IonSelect>
              </IonItem>
            </div>
            <p className={`estimate-note ${locClass}`}>{locNote}</p>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader><IonCardTitle>Scope of work</IonCardTitle></IonCardHeader>
          <IonCardContent>
            <p className="estimate-help">Description of work that will appear on the proposal. Include any info that will explain the scope to the customer that will eliminate any follow up questions.</p>
            <IonTextarea autoGrow rows={5} fill="outline" value={d.scope} onIonInput={(e) => set({ scope: String(e.detail.value ?? '') })}
              placeholder="e.g. Furnish and install new 1/2 HP condenser fan motor on RTU-3. Recover refrigerant as required, verify operation and amp draw on start-up." />
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader><IonCardTitle>Material</IonCardTitle></IonCardHeader>
          <IonCardContent>
            {d.materials.map((m, i) => (
              <div className="estimate-line" key={i}>
                <div className="estimate-line-fields material">
                  <IonItem><IonInput label="Material description" labelPlacement="stacked" value={m.description}
                    onIonInput={(e) => set({ materials: d.materials.map((x, j) => (j === i ? { ...x, description: String(e.detail.value ?? '') } : x)) })} /></IonItem>
                  <IonItem><IonInput label="Alpha cost ($)" labelPlacement="stacked" inputmode="decimal" value={m.alpha_cost}
                    onIonInput={(e) => set({ materials: d.materials.map((x, j) => (j === i ? { ...x, alpha_cost: String(e.detail.value ?? '') } : x)) })} /></IonItem>
                </div>
                <IonButton fill="clear" color="medium" aria-label="Remove material line"
                  onClick={() => set({ materials: d.materials.length > 1 ? d.materials.filter((_, j) => j !== i) : [blankMaterial()] })}>×</IonButton>
                <div className="estimate-calc">
                  <span>Markup <b>{c.materialLines[i]?.alphaCost ? pct(c.materialLines[i].markupRate) : '—'}</b></span>
                  <span>Marked up <b>{money(c.materialLines[i]?.markedUp)}</b></span>
                  <span>After tax <b>{money(c.materialLines[i]?.afterTax)}</b></span>
                </div>
              </div>
            ))}
            <IonButton fill="outline" size="small" onClick={() => set({ materials: [...d.materials, blankMaterial()] })}>+ Add material line</IonButton>
            <p className="estimate-help">Markup by each line&apos;s Alpha cost: under $101 → 100%, $101–300 → 80%, $301–1,000 → 60%, $1,001–2,000 → 40%, $2,001 and up → 25%. Tax applies to material only.</p>
            <div className="estimate-subtotal"><span>Total material (marked up, after tax)</span><span>{money(c.materialTotal)}</span></div>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader><IonCardTitle>Labor</IonCardTitle></IonCardHeader>
          <IonCardContent>
            {d.labor.map((l, i) => (
              <div className="estimate-line" key={i}>
                <div className="estimate-line-fields labor">
                  <IonItem><IonInput label="Labor description" labelPlacement="stacked" value={l.description}
                    onIonInput={(e) => set({ labor: d.labor.map((x, j) => (j === i ? { ...x, description: String(e.detail.value ?? '') } : x)) })} /></IonItem>
                  <IonItem><IonInput label="Hours" labelPlacement="stacked" inputmode="decimal" value={l.hours}
                    onIonInput={(e) => set({ labor: d.labor.map((x, j) => (j === i ? { ...x, hours: String(e.detail.value ?? '') } : x)) })} /></IonItem>
                  <IonItem>
                    <IonSelect label="Labor type" labelPlacement="stacked" value={l.labor_type}
                      onIonChange={(e) => set({ labor: d.labor.map((x, j) => (j === i ? { ...x, labor_type: String(e.detail.value) } : x)) })}>
                      {bundle.labor_types.map((t) => <IonSelectOption key={t.code} value={t.code}>{t.code}</IonSelectOption>)}
                    </IonSelect>
                  </IonItem>
                </div>
                <IonButton fill="clear" color="medium" aria-label="Remove labor line"
                  onClick={() => set({ labor: d.labor.length > 1 ? d.labor.filter((_, j) => j !== i) : [blankLabor()] })}>×</IonButton>
                <div className="estimate-calc">
                  <span>Rate <b>{money(c.laborLines[i]?.rate)}/hr</b></span>
                  <span>Line total <b>{money(c.laborLines[i]?.lineTotal)}</b></span>
                </div>
              </div>
            ))}
            <IonButton fill="outline" size="small" onClick={() => set({ labor: [...d.labor, blankLabor()] })}>+ Add labor line</IonButton>
            <div className="estimate-subtotal"><span>Total labor · {c.laborHours} hr</span><span>{money(c.laborTotal)}</span></div>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader><IonCardTitle>Freight and discount</IonCardTitle></IonCardHeader>
          <IonCardContent>
            <IonItem lines="none">
              <IonCheckbox checked={d.freightOn} onIonChange={(e) => set({ freightOn: e.detail.checked })} labelPlacement="end">Freight (if required) · % of total material after tax</IonCheckbox>
            </IonItem>
            <div className="estimate-grid">
              <IonItem><IonInput label="Freight %" labelPlacement="stacked" inputmode="decimal" value={d.freightPct} disabled={!d.freightOn} onIonInput={(e) => set({ freightPct: String(e.detail.value ?? '') })} /></IonItem>
              <IonItem><IonInput label="Customer discount ($)" labelPlacement="stacked" inputmode="decimal" value={d.discount} onIonInput={(e) => set({ discount: String(e.detail.value ?? '') })} /></IonItem>
            </div>
            <div className="estimate-subtotal"><span>Freight</span><span>{money(c.freight)}</span></div>
          </IonCardContent>
        </IonCard>

        <IonCard>
          <IonCardHeader><IonCardTitle>Assumptions</IonCardTitle></IonCardHeader>
          <IonCardContent>
            <p className="estimate-help">Information not included in scope but important, like lead time or exclusions.</p>
            {d.assumptions.map((a, i) => (
              <div className="estimate-assumption" key={i}>
                <span aria-hidden="true">•</span>
                <IonTextarea autoGrow rows={1} fill="outline" value={a} aria-label="Assumption"
                  onIonInput={(e) => set({ assumptions: d.assumptions.map((x, j) => (j === i ? String(e.detail.value ?? '') : x)) })} />
                <IonButton fill="clear" color="medium" aria-label="Remove assumption" onClick={() => set({ assumptions: d.assumptions.filter((_, j) => j !== i) })}>×</IonButton>
              </div>
            ))}
            <IonButton fill="outline" size="small" onClick={() => set({ assumptions: [...d.assumptions, ''] })}>+ Add assumption</IonButton>
          </IonCardContent>
        </IonCard>
      </fieldset>

      <IonCard className="estimate-margin">
        <IonCardHeader className="estimate-status-head">
          <IonCardTitle>Margin check</IonCardTitle><IonChip color="warning">Internal · not on proposal</IonChip>
        </IonCardHeader>
        <IonCardContent className="estimate-kpis">
          <div><IonNote>Total price</IonNote><strong>{money(c.totalPrice)}</strong><IonNote>rounded up to $5</IonNote></div>
          <div><IonNote>Labor cost</IonNote><strong>{money(c.internalLaborCost)}</strong><IonNote>{c.laborHours} hr × {money(rates.internalLaborCost)}</IonNote></div>
          <div><IonNote>Material cost</IonNote><strong>{money(c.internalMaterialCost)}</strong><IonNote>Alpha cost + freight, taxed</IonNote></div>
          <div><IonNote>Project margin</IonNote><strong>{pct(c.margin)}</strong><IonNote>on the rounded total</IonNote></div>
        </IonCardContent>
      </IonCard>

      {estimate && (
        <IonCard>
          <IonCardHeader><IonCardTitle>Proposal</IonCardTitle></IonCardHeader>
          <IonCardContent>
            {!canPropose && estimate.status !== 'accepted' ? (
              <p className="estimate-note warn">
                {!estimate.acs_number ? 'Enter the ACS# and save to make the proposal.' : estimate.is_expired ? 'This proposal has expired. Change the date and save to re-issue it.' : 'The customer declined this proposal.'}
              </p>
            ) : (
              <>
                <p className="estimate-note">{estimate.status === 'accepted' ? 'Signed. Download or email the customer their signed copy.' : `Total ${money(estimate.total_price)} · valid until ${shortDate(estimate.valid_until)}. Save any changes first.`}</p>
                <div className="estimate-actions">
                  <IonButton onClick={openPdf}>{estimate.status === 'accepted' ? 'Signed PDF' : 'Open PDF to print or save'}</IonButton>
                  <IonButton fill="outline" onClick={() => { setEmail({ to: estimate.contact_email || estimate.customer_detail.contact_email, cc: '', message: '' }); setEmailOpen(true); }}>Email to customer</IonButton>
                </div>
              </>
            )}
          </IonCardContent>
        </IonCard>
      )}

      {estimate && (canPropose || locked) && (
        <AcceptancePanel estimate={estimate} onDone={onSaved} notify={notify} />
      )}

      <IonModal isOpen={emailOpen} onDidDismiss={() => setEmailOpen(false)}>
        <div className="estimate-email">
          <h2>Email the proposal</h2>
          <IonItem><IonInput label="To" labelPlacement="stacked" type="email" value={email.to} onIonInput={(e) => setEmail({ ...email, to: String(e.detail.value ?? '') })} /></IonItem>
          <IonItem><IonInput label="Cc (optional)" labelPlacement="stacked" value={email.cc} onIonInput={(e) => setEmail({ ...email, cc: String(e.detail.value ?? '') })} /></IonItem>
          <IonItem><IonTextarea label="Message (optional)" labelPlacement="stacked" autoGrow value={email.message} onIonInput={(e) => setEmail({ ...email, message: String(e.detail.value ?? '') })} /></IonItem>
          <IonLabel className="estimate-help">The PDF is attached. The customer can reply to accept, or sign with you on site.</IonLabel>
          <div className="estimate-actions">
            <IonButton onClick={sendEmail}>Send</IonButton>
            <IonButton fill="outline" onClick={() => setEmailOpen(false)}>Cancel</IonButton>
          </div>
        </div>
      </IonModal>
    </div>
  );
};

export default EstimateEditor;
